import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Hidden,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@material-ui/core'
import { GridColDef, GridRowParams, GridSortModel, GridValueFormatterParams } from '@material-ui/data-grid'
import EmailIcon from '@material-ui/icons/Email'
import PhoneIcon from '@material-ui/icons/Phone'
import RefreshIcon from '@material-ui/icons/Refresh'
import AddIcon from '@material-ui/icons/Add'
import { Alert } from '@material-ui/lab'
import { Booking, Booking as BookingModel, BookingStatus } from '@sugg-gestion/suggpro-graph-module'
import clsx from 'clsx'
import _ from 'lodash'
import moment from 'moment'
import React from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { usePhoneNumber } from 'core/services/phone/usePhoneNumber'
import ResponsibleDataGrid from '../../common/responsibleDataGrid'
import SubmitButton from '../../common/submitButton'
import { useStyles } from './styles.css'
import BookingForm, { BookingFormState } from '../../forms/booking/bookingForm'
import CloseButton from '../../dialogs/closeButton'

interface Props {
    bookings: Array<BookingModel>
    historyMonths: number
    loading: boolean
    acceptBookingInProgress?: string
    rejectBookingInProgress?: string
    onAcceptBooking: (bookingId: string) => void
    onRejectBooking: (bookingId: string) => void
    onEditBooking: (bookingId: string, values: BookingFormState) => Promise<void>
    bookingEditInProgress: boolean
    onAddBooking: (values: BookingFormState) => Promise<void>
    bookingAddInProgress: boolean
    onRefresh: () => void
}

const BookingView: React.FC<Props> = ({
    bookings,
    historyMonths,
    acceptBookingInProgress,
    onAcceptBooking,
    rejectBookingInProgress,
    onRejectBooking,
    onEditBooking,
    bookingEditInProgress,
    loading,
    onRefresh,
    onAddBooking,
    bookingAddInProgress,
}) => {
    const classes = useStyles()
    const { t } = useTranslation()
    const { validPhone, getPhoneNumber, displayPhone } = usePhoneNumber()
    const [sortModel, setSortModel] = React.useState<GridSortModel>([
        {
            field: 'createdAt',
            sort: 'desc',
        },
    ])

    const theme = useTheme()
    const fullScreen = useMediaQuery(theme.breakpoints.down('xs'))

    const handleUpdateSortModel = (model: GridSortModel) => {
        if (!_.isEqual(model, sortModel)) {
            setSortModel(model)
        }
    }

    const [openEditBooking, setOpenEditBooking] = React.useState<Booking | undefined>()
    const handleEditBooking = (bookingId: string, values: BookingFormState) => {
        return onEditBooking(bookingId, values).then(onCloseEditBooking)
    }

    const onOpenEditBooking = (bookingId: string) => {
        const booking = bookings.find((booking) => booking.id === bookingId)
        if (booking) {
            setOpenEditBooking(booking)
        }
    }

    const onCloseEditBooking = () => {
        setOpenEditBooking(undefined)
    }

    const [openAddBooking, setOpenAddBooking] = React.useState<boolean>(false)

    const onOpenAddBooking = () => {
        setOpenAddBooking(true)
    }

    const onCloseAddBooking = () => {
        setOpenAddBooking(false)
    }
    const handleAddBooking = (values: BookingFormState) => {
        return onAddBooking(values).then(onCloseAddBooking)
    }

    const columns: GridColDef[] = [
        {
            field: 'createdAt',
            headerName: t('views.booking.createdAt'),
            width: 150,
            type: 'dateTime',
            renderCell: ({ value }) => {
                const date = moment(value as string)
                return date.format('DD/MM/yy à HH:mm')
            },
        },
        {
            field: 'name',
            headerName: t('views.booking.name'),
            width: 150,
            renderCell: (params: GridValueFormatterParams) => {
                const booking = params.row as BookingModel
                return booking.givenName + ' ' + booking.familyName
            },
        },
        {
            field: 'email',
            headerName: t('views.booking.email'),
            width: 250,
            renderCell: ({ value }) => {
                return (
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <Button variant="outlined" className={classes.iconButton} href={'mailto:' + value}>
                            <EmailIcon fontSize="small" />
                        </Button>
                        <span title={value as string}>{value}</span>
                    </div>
                )
            },
        },
        {
            field: 'phone',
            headerName: t('views.booking.phone'),
            width: 150,
            renderCell: ({ value }) => {
                if (validPhone(value as string | undefined)) {
                    return (
                        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                            <Button
                                variant="outlined"
                                className={classes.iconButton}
                                href={getPhoneNumber(value as string | undefined) ?? ''}
                            >
                                <PhoneIcon fontSize="small" />
                            </Button>
                            <span title={displayPhone(value as string | undefined)}>
                                {displayPhone(value as string | undefined)}
                            </span>
                        </div>
                    )
                }
                return value
            },
        },
        { field: 'guestsNumber', headerName: t('views.booking.guests'), width: 150 },
        {
            field: 'bookingDate',
            headerName: t('views.booking.date'),
            width: 150,
            type: 'dateTime',
            renderCell: ({ value }) => {
                const date = moment(value as string)
                return date.format('DD/MM/yy à HH:mm')
            },
        },
        {
            field: 'comment',
            sortable: false,
            headerName: t('views.booking.comment'),
            width: 330,
            renderCell: ({ value }) => (
                <>
                    <Hidden only={['xs', 'sm']}>
                        <Tooltip title={<Typography>{value}</Typography>}>
                            <span className={classes.text}>{value}</span>
                        </Tooltip>
                    </Hidden>
                    <Hidden only={['md', 'lg', 'xl']}>
                        <Typography>{value}</Typography>
                    </Hidden>
                </>
            ),
        },
        {
            field: 'actions',
            filterable: false,
            sortable: false,
            headerName: t('views.booking.validation'),
            cellClassName: classes.actions,
            width: 240,
            renderCell: (params: GridValueFormatterParams) => {
                const booking = params.row as BookingModel
                const acceptButton = (
                    <SubmitButton
                        loading={acceptBookingInProgress === booking.id}
                        onClick={() => onAcceptBooking(booking.id)}
                        variant="contained"
                        color="primary"
                        size="small"
                        disabled={loading || rejectBookingInProgress === booking.id}
                    >
                        <Trans i18nKey="actions.accept" />
                    </SubmitButton>
                )
                const rejectButton = (
                    <SubmitButton
                        loading={rejectBookingInProgress === booking.id}
                        onClick={() => onRejectBooking(booking.id)}
                        variant="contained"
                        color="secondary"
                        size="small"
                        disabled={loading || acceptBookingInProgress === booking.id}
                    >
                        <Trans i18nKey="actions.reject" />
                    </SubmitButton>
                )
                const editButton = (
                    <SubmitButton
                        onClick={() => onOpenEditBooking(booking.id)}
                        variant="outlined"
                        color="primary"
                        size="small"
                        className={classes.editButton}
                    >
                        <Trans i18nKey="actions.edit" />
                    </SubmitButton>
                )
                switch (booking.status) {
                    case BookingStatus.New:
                        return (
                            <>
                                {acceptButton}
                                {rejectButton}
                                {editButton}
                            </>
                        )
                    case BookingStatus.Confirmed:
                        return (
                            <>
                                {rejectButton}
                                {editButton}
                            </>
                        )
                    case BookingStatus.Rejected:
                        return (
                            <>
                                {acceptButton}
                                {editButton}
                            </>
                        )
                }
            },
        },
    ]

    return (
        <>
            <div className={clsx(classes.header, classes.actions)}>
                <Typography variant="h1" className={classes.title}>
                    <Trans i18nKey="views.booking.bookings" />
                </Typography>
                <div>
                    <SubmitButton
                        loading={loading}
                        onClick={onOpenAddBooking}
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                    >
                        <Trans i18nKey="views.booking.add" />
                    </SubmitButton>
                    <SubmitButton
                        loading={loading}
                        onClick={onRefresh}
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                    >
                        <Trans i18nKey="actions.refresh" />
                    </SubmitButton>
                </div>
            </div>
            <Alert severity="info" className={classes.alert}>
                <Trans i18nKey="views.booking.historyLimit" values={{ months: historyMonths }} />
            </Alert>
            <div className={classes.root}>
                <div className={classes.tableContainer}>
                    <ResponsibleDataGrid
                        primaryKey="name"
                        secondaryKey="bookingDate"
                        className={classes.grid}
                        columns={columns}
                        rows={bookings}
                        pageSize={25}
                        rowsPerPageOptions={[25]}
                        loading={loading}
                        disableSelectionOnClick
                        sortModel={sortModel}
                        onSortModelChange={handleUpdateSortModel}
                        getRowClassName={(params: GridRowParams) => {
                            return params.row.status === BookingStatus.Confirmed
                                ? classes.confirmed
                                : params.row.status === BookingStatus.Rejected
                                ? classes.rejected
                                : classes.new
                        }}
                        getPrimaryHeaderClassName={(params: GridRowParams) => {
                            return params.row.status === BookingStatus.Confirmed
                                ? classes.confirmed
                                : params.row.status === BookingStatus.Rejected
                                ? classes.rejected
                                : classes.new
                        }}
                        getSecondaryHeaderClassName={(params: GridRowParams) => {
                            return params.row.status === BookingStatus.Confirmed
                                ? classes.confirmed
                                : params.row.status === BookingStatus.Rejected
                                ? classes.rejected
                                : classes.new
                        }}
                    />
                </div>
            </div>
            <Dialog
                open={openEditBooking !== undefined}
                onClose={onCloseEditBooking}
                fullScreen={fullScreen}
                scroll="paper"
                classes={{
                    paper: classes.paper,
                }}
                maxWidth="md"
            >
                {openEditBooking && (
                    <BookingForm
                        onSubmit={(values) => handleEditBooking(openEditBooking?.id, values)}
                        initialValues={{
                            givenName: openEditBooking.givenName,
                            familyName: openEditBooking.givenName,
                            email: openEditBooking.email,
                            phone: openEditBooking.phone,
                            date: moment(openEditBooking.bookingDate),
                            time: moment(openEditBooking.bookingDate),
                            guests: openEditBooking.guestsNumber,
                            comment: openEditBooking.comment ?? '',
                            sendEmail: false,
                        }}
                        inProgress={loading || bookingEditInProgress}
                        withMinDate={false}
                        withSendEmail={false}
                    >
                        {(content, actions) => (
                            <>
                                <DialogTitle className={classes.title}>
                                    <Trans i18nKey="views.externalLinks.customization.title" />
                                </DialogTitle>
                                <DialogContent dividers>
                                    <CloseButton onClose={onCloseEditBooking} />
                                    {content}
                                </DialogContent>
                                <DialogActions className={classes.actions}>
                                    <Button variant="contained" onClick={onCloseEditBooking}>
                                        <Trans i18nKey="actions.cancel" />
                                    </Button>
                                    {actions.map((action, index) => (
                                        <SubmitButton
                                            key={index}
                                            variant="contained"
                                            color="primary"
                                            {...action}
                                            className={clsx(action.className)}
                                        >
                                            <Trans i18nKey="actions.validate" />
                                        </SubmitButton>
                                    ))}
                                </DialogActions>
                            </>
                        )}
                    </BookingForm>
                )}
            </Dialog>
            <Dialog
                open={openAddBooking}
                onClose={onCloseAddBooking}
                fullScreen={fullScreen}
                scroll="paper"
                classes={{
                    paper: classes.paper,
                }}
                maxWidth="md"
            >
                <BookingForm
                    onSubmit={handleAddBooking}
                    initialValues={{
                        givenName: '',
                        familyName: '',
                        email: '',
                        phone: '',
                        date: moment(),
                        time: moment(),
                        guests: 2,
                        comment: '',
                        sendEmail: false,
                    }}
                    inProgress={loading || bookingAddInProgress}
                    withSendEmail={true}
                >
                    {(content, actions) => (
                        <>
                            <DialogTitle className={classes.title}>
                                <Trans i18nKey="views.booking.add" />
                            </DialogTitle>
                            <DialogContent dividers>
                                <CloseButton onClose={onCloseAddBooking} />
                                {content}
                            </DialogContent>
                            <DialogActions className={classes.actions}>
                                <Button variant="contained" onClick={onCloseAddBooking}>
                                    <Trans i18nKey="actions.cancel" />
                                </Button>
                                {actions.map((action, index) => (
                                    <SubmitButton
                                        key={index}
                                        variant="contained"
                                        color="primary"
                                        {...action}
                                        className={clsx(action.className)}
                                    >
                                        <Trans i18nKey="actions.validate" />
                                    </SubmitButton>
                                ))}
                            </DialogActions>
                        </>
                    )}
                </BookingForm>
                {openEditBooking && (
                    <BookingForm
                        onSubmit={(values) => handleEditBooking(openEditBooking?.id, values)}
                        initialValues={{
                            givenName: openEditBooking.givenName,
                            familyName: openEditBooking.givenName,
                            email: openEditBooking.email,
                            phone: openEditBooking.phone,
                            date: moment(openEditBooking.bookingDate),
                            time: moment(openEditBooking.bookingDate),
                            guests: openEditBooking.guestsNumber,
                            comment: openEditBooking.comment ?? '',
                            sendEmail: false,
                        }}
                        inProgress={bookingEditInProgress}
                        withMinDate={false}
                        withSendEmail={false}
                    >
                        {(content, actions) => (
                            <>
                                <DialogTitle className={classes.title}>
                                    <Trans i18nKey="views.booking.edit" />
                                </DialogTitle>
                                <DialogContent dividers>
                                    <CloseButton onClose={onCloseEditBooking} />
                                    {content}
                                </DialogContent>
                                <DialogActions className={classes.actions}>
                                    <Button variant="contained" onClick={onCloseEditBooking}>
                                        <Trans i18nKey="actions.cancel" />
                                    </Button>
                                    {actions.map((action, index) => (
                                        <SubmitButton
                                            key={index}
                                            variant="contained"
                                            color="primary"
                                            {...action}
                                            className={clsx(action.className)}
                                        >
                                            <Trans i18nKey="actions.validate" />
                                        </SubmitButton>
                                    ))}
                                </DialogActions>
                            </>
                        )}
                    </BookingForm>
                )}
            </Dialog>
        </>
    )
}

export default BookingView
